import React, { useEffect, useState, useRef, useMemo } from 'react';
import Axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import InvoicePreview from './InvoicePreview';

const History = ({ onLoadInvoice }) => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const invoiceRef = useRef();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    const userId = localStorage.getItem("userId");
    try {
      const res = await Axios.get(`http://localhost:3001/api/invoices/${userId}`);
      const baseInvoices = res.data;

      const fullData = await Promise.all(baseInvoices.map(async (inv) => {
        try {
          const [itemsRes, transRes] = await Promise.all([
            Axios.get(`http://localhost:3001/api/invoice-items/${inv.id}`),
            Axios.get(`http://localhost:3001/api/invoice-payments/${inv.id}`).catch(() => ({ data: [] }))
          ]);

          // Use 'let' instead of 'const' if you plan to reassign, 
          // though usually, we just calculate the final value once:
          const items = Array.isArray(itemsRes.data) ? itemsRes.data : [];
          const payments = Array.isArray(transRes.data) ? transRes.data : [];

          const subtotal = items.reduce((sum, item) => sum + (Number(item.rate || 0) * Number(item.qty || 0)), 0);
          const taxRate = Number(inv.tax_rate) || 0;
          const discount = Number(inv.discount) || 0;
          const Gtotal = subtotal - discount;
          const Taxtotal = (Gtotal * taxRate)/100;

          
          const totalAmount = (Gtotal + Taxtotal);
          const totalPaid = payments.reduce((sum, t) => sum + Number(t.amount || 0), 0);

          let status = "Unpaid";

          // FIX: Use === for comparison, not = for assignment
          if (totalAmount <= 0) {
            status = "Paid";
          } else if (totalPaid === totalAmount) {
            status = "Paid";
          } else if (totalPaid < totalAmount && totalPaid > 0 && totalPaid !== totalAmount ) {
            status = "Partial";
          } else if (totalPaid > totalAmount ) {
            status = "Over Paid";
          } else {
            status = "Unpaid";
          }

          return { ...inv, status };
        } catch (err) {
          console.error("Row calculation error:", err);
          return { ...inv, status: "Unpaid" };
        }
      }));

      setInvoices(fullData);
      setLoading(false);
    } catch (e) {
      console.error("Fetch error:", e);
      setLoading(false);
    }
  };

  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      return b.invoice_no.localeCompare(a.invoice_no, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [invoices]);

  // Helper for Status Badge Colors - Defaults to Unpaid style if status is null
  const getStatusStyle = (status) => {
    const currentStatus = status || "Unpaid"; // Safety check: if null then unpaid
    
    const colors = {
      'Paid': { bg: '#00ff04', text: '#000000', border: '#001208'  },
      'Partial': { bg: '#ffae00', text: '#000000', border: '#f5f50b' },
      'Unpaid': { bg: '#da0000', text: '#ffffff', border: '#f87171' },
      'Over Paid': { bg: '#5100ff', text: '#ffd500', border: '#7605ff' }
    };

    const style = colors[currentStatus] || colors['Unpaid'];

    return {
      backgroundColor: style.bg,
      color: style.text,
      border: `1px solid ${style.border}`,
      padding: '4px 10px',
      borderRadius: '6px',
      fontWeight: 'bold',
      fontSize: '11px',
      display: 'inline-block',
      textAlign: 'center',
      textTransform: 'uppercase',
      minWidth: '70px'
    };
  };

  const handleEditClick = async (inv) => {
    try {
      const [itemsRes, transRes] = await Promise.all([
        Axios.get(`http://localhost:3001/api/invoice-items/${inv.id}`),
        Axios.get(`http://localhost:3001/api/invoice-payments/${inv.id}`).catch(() => ({ data: [] }))
      ]);

      const editData = {
        ...inv,
        id: inv.id,
        invoiceNo: inv.invoice_no,
        clientName: inv.client_name,
        company: inv.company_name,
        address: inv.address,
        directorName: inv.director_name,
        directorTitle: inv.director_title,
        signature: inv.signature_image,
        items: itemsRes.data.map(item => ({ desc: item.description, rate: Number(item.rate), qty: item.qty })),
        transactions: transRes.data.map(t => ({ 
          date: t.payment_date ? t.payment_date.split('T')[0] : "", 
          method: t.method, 
          amount: Number(t.amount) 
        })),
        discount: Number(inv.discount || 0),
        vatRate: Number(inv.tax_rate || 15),
        currency: inv.currency || "BDT",
        currencySymbol: inv.currency_symbol || "TK"
      };
      onLoadInvoice(editData); 
    } catch (e) {
      console.error(e);
      alert("Failed to load invoice items.");
    }
  };

  const handleDownloadPDF = async () => {
    const element = invoiceRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
    pdf.save(`Invoice_${selectedInvoice.invoice_no}.pdf`);
  };

  const handleViewClick = async (inv) => {
    try {
      const [itemsRes, transRes] = await Promise.all([
        Axios.get(`http://localhost:3001/api/invoice-items/${inv.id}`),
        Axios.get(`http://localhost:3001/api/invoice-payments/${inv.id}`).catch(() => ({ data: [] }))
      ]);

      const previewData = {
        ...inv,
        invoiceNo: inv.invoice_no,
        clientName: inv.client_name,
        company: inv.company_name,
        address: inv.address,
        directorName: inv.director_name,
        directorTitle: inv.director_title,
        signature: inv.signature_image,
        companyFooter: inv.company_footer,
        issueDate: inv.issue_date ? inv.issue_date.split('T')[0] : "",
        dueDate: inv.due_date ? inv.due_date.split('T')[0] : "",
        vatRate: inv.tax_rate,
        discount: inv.discount,
        membershipType: inv.membership_type,
        items: itemsRes.data.map(i => ({ desc: i.description, rate: i.rate, qty: i.qty })),
        transactions: transRes.data.map(t => ({ date: t.payment_date, method: t.method, amount: t.amount }))
      };
      setSelectedInvoice(previewData);
    } catch (e) { console.error(e); }
  };

  if (loading) return <div style={{ padding: '20px', color: '#8696a0' }}>Loading history...</div>;

  return (
    <div style={{ padding: '20px' }}>
      <h3 style={{ color: '#fff', marginBottom: '20px' }}>Invoice History</h3>
      
      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#011433', color: 'white', borderRadius: '8px' }}>
        <thead>
          <tr style={{ textAlign: 'left', backgroundColor: '#000b1d' }}>
            <th style={{ padding: '15px' }}>INV # ▼</th>
            <th style={{ padding: '15px' }}>DATE</th>
            <th style={{ padding: '15px' }}>CLIENT</th>
            <th style={{ padding: '15px' }}>STATUS</th>
            <th style={{ padding: '15px', textAlign: 'right' }}>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {sortedInvoices.map(inv => (
            <tr key={inv.id} style={{ borderBottom: '1px solid #2a3942' }}>
              <td style={{ padding: '15px', fontWeight: 'bold' }}>{inv.invoice_no}</td>
              <td style={{ padding: '15px', color: '#8696a0' }}>
                {inv.issue_date ? inv.issue_date.split('T')[0] : '---'}
              </td>
              <td style={{ padding: '15px' }}>{inv.client_name}</td>
              <td style={{ padding: '15px' }}>
                {/* Fallback applied here: if inv.status is missing, it shows Unpaid */}
                <span style={getStatusStyle(inv.status)}>{inv.status || "Unpaid"}</span>
              </td>
              <td style={{ padding: '15px', textAlign: 'right' }}>
                <button onClick={() => handleViewClick(inv)} style={actionBtn('#004ef5')}>View</button>
                <button onClick={() => handleEditClick(inv)} style={actionBtn('#ff4d00', '10px')}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selectedInvoice && (
        <div style={modalOverlayStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '210mm', marginBottom: '15px' }}>
             <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={handleDownloadPDF} style={toolBtn('#00a884')}>Download PDF</button>
                <button onClick={() => window.print()} style={toolBtn('#160E44')}>Print</button>
             </div>
             <button onClick={() => setSelectedInvoice(null)} style={toolBtn('#ef4444')}>Close</button>
          </div>
          <div style={{ boxShadow: '0 0 50px rgba(0,0,0,0.5)' }}>
            <InvoicePreview ref={invoiceRef} data={selectedInvoice} />
          </div>
        </div>
      )}
    </div>
  );
};

const actionBtn = (bg, ml = '0px') => ({ backgroundColor: bg, color: 'white', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', marginLeft: ml });
const toolBtn = (bg) => ({ backgroundColor: bg, color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' });
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(11, 20, 26, 0.98)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', zIndex: 1000, overflowY: 'auto' };

export default History;